import React, { useEffect, useState } from 'react';
import { apiClient } from '../../services/api';

// ── Types ──────────────────────────────────────────────────────────

interface ClassifyResult {
  restrictionType: 'ban' | 'restriction' | 'none';
  confidence: number;
  reasoning: string;
  suggestedNotes: string;
}

interface ResearchResult {
  answer: string;
  citedLaws: Array<{
    id: string;
    state: string;
    city?: string;
    restrictionType: string;
    notes?: string;
  }>;
}

interface SummaryResult {
  summary: string;
  keyPoints: string[];
  affectedJurisdictions: string[];
  permitRequired: boolean | null;
}

// ── Component ──────────────────────────────────────────────────────

const ZoningAIPanel: React.FC = () => {
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const [activeSection, setActiveSection] = useState<'research' | 'classify' | 'summarise'>('research');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Research state
  const [question, setQuestion] = useState('');
  const [researchState, setResearchState] = useState('');
  const [researchCity, setResearchCity] = useState('');
  const [researchResult, setResearchResult] = useState<ResearchResult | null>(null);

  // Classify state
  const [classifyText, setClassifyText] = useState('');
  const [classifyResult, setClassifyResult] = useState<ClassifyResult | null>(null);

  // Summarise state
  const [summariseText, setSummariseText] = useState('');
  const [summaryResult, setSummaryResult] = useState<SummaryResult | null>(null);

  useEffect(() => {
    apiClient
      .get('/admin/zoning/ai/status')
      .then((res) => setAiEnabled(res.data.enabled))
      .catch(() => setAiEnabled(false));
  }, []);

  const handleResearch = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setResearchResult(null);
    try {
      const res = await apiClient.post('/admin/zoning/ai/research', {
        question,
        state: researchState || undefined,
        city: researchCity || undefined,
      });
      setResearchResult(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Research failed';
      setError(msg);
    }
    setLoading(false);
  };

  const handleClassify = async () => {
    if (!classifyText.trim()) return;
    setLoading(true);
    setError(null);
    setClassifyResult(null);
    try {
      const res = await apiClient.post('/admin/zoning/ai/classify', { text: classifyText });
      setClassifyResult(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Classification failed';
      setError(msg);
    }
    setLoading(false);
  };

  const handleSummarise = async () => {
    if (!summariseText.trim()) return;
    setLoading(true);
    setError(null);
    setSummaryResult(null);
    try {
      const res = await apiClient.post('/admin/zoning/ai/summarise', { text: summariseText });
      setSummaryResult(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Summarisation failed';
      setError(msg);
    }
    setLoading(false);
  };

  if (aiEnabled === null) {
    return <div className="text-sm text-slate-400 p-4">Checking AI status...</div>;
  }

  if (!aiEnabled) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
        <strong>AI Zoning features are not available.</strong> Set <code className="bg-amber-100 px-1 rounded">OPENAI_API_KEY</code> in your <code>.env</code> file to enable.
      </div>
    );
  }

  const sections: { key: typeof activeSection; label: string; icon: string }[] = [
    { key: 'research', label: 'Ask a Question', icon: '💬' },
    { key: 'classify', label: 'Classify Text', icon: '🏷️' },
    { key: 'summarise', label: 'Summarise Legal Text', icon: '📋' },
  ];

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      {/* Section selector */}
      <div className="flex border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
        {sections.map((s) => (
          <button
            key={s.key}
            onClick={() => { setActiveSection(s.key); setError(null); }}
            className={`flex-1 px-4 py-3 text-sm font-medium transition border-b-2 ${
              activeSection === s.key
                ? 'border-violet-600 text-violet-700 bg-white dark:bg-slate-900'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            <span className="mr-1.5">{s.icon}</span>
            {s.label}
          </button>
        ))}
      </div>

      <div className="p-5 space-y-4">
        {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</p>}

        {/* ═══ Research ═══ */}
        {activeSection === 'research' && (
          <>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Ask a natural-language question about mobile billboard zoning. The AI will search your zoning law database and provide an answer with citations.
            </p>
            <div className="grid grid-cols-4 gap-3">
              <input
                value={researchState}
                onChange={(e) => setResearchState(e.target.value)}
                placeholder="State filter (optional)"
                className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
              />
              <input
                value={researchCity}
                onChange={(e) => setResearchCity(e.target.value)}
                placeholder="City filter (optional)"
                className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
              />
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleResearch()}
                placeholder="e.g. Can I run a mobile billboard in Austin, TX?"
                className="col-span-2 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={handleResearch}
              disabled={loading || !question.trim()}
              className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {loading ? 'Thinking...' : 'Ask AI'}
            </button>

            {researchResult && (
              <div className="space-y-3">
                <div className="rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {researchResult.answer}
                </div>
                {researchResult.citedLaws.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-1">Referenced Laws</h4>
                    <div className="space-y-1">
                      {researchResult.citedLaws.map((l) => (
                        <div key={l.id} className="flex items-center gap-2 text-xs">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            l.restrictionType === 'ban' ? 'bg-red-100 text-red-700' :
                            l.restrictionType === 'restriction' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }`}>{l.restrictionType}</span>
                          <span className="font-medium text-slate-700 dark:text-slate-300">{l.state}{l.city ? ` / ${l.city}` : ''}</span>
                          {l.notes && <span className="text-slate-500 dark:text-slate-400 truncate max-w-xs" title={l.notes}>{l.notes}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══ Classify ═══ */}
        {activeSection === 'classify' && (
          <>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Paste raw ordinance or legal text and the AI will classify it as a ban, restriction, or no restriction on mobile billboards.
            </p>
            <textarea
              value={classifyText}
              onChange={(e) => setClassifyText(e.target.value)}
              placeholder="Paste ordinance text here..."
              rows={5}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
            />
            <button
              onClick={handleClassify}
              disabled={loading || classifyText.trim().length < 10}
              className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {loading ? 'Classifying...' : 'Classify'}
            </button>

            {classifyResult && (
              <div className="rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                    classifyResult.restrictionType === 'ban' ? 'bg-red-100 text-red-700' :
                    classifyResult.restrictionType === 'restriction' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>{classifyResult.restrictionType.toUpperCase()}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Confidence: {(classifyResult.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300">{classifyResult.reasoning}</p>
                {classifyResult.suggestedNotes && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-2">
                    <strong>Suggested notes:</strong> {classifyResult.suggestedNotes}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══ Summarise ═══ */}
        {activeSection === 'summarise' && (
          <>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Paste legal text and the AI will extract a plain-English summary, key facts, affected jurisdictions, and permit requirements.
            </p>
            <textarea
              value={summariseText}
              onChange={(e) => setSummariseText(e.target.value)}
              placeholder="Paste legal / ordinance text here..."
              rows={5}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
            />
            <button
              onClick={handleSummarise}
              disabled={loading || summariseText.trim().length < 10}
              className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {loading ? 'Summarising...' : 'Summarise'}
            </button>

            {summaryResult && (
              <div className="rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <p className="text-sm text-slate-800 dark:text-slate-200">{summaryResult.summary}</p>

                {summaryResult.keyPoints.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-1">Key Points</h4>
                    <ul className="list-disc list-inside text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                      {summaryResult.keyPoints.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}

                {summaryResult.affectedJurisdictions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-1">Affected Jurisdictions</h4>
                    <div className="flex flex-wrap gap-1">
                      {summaryResult.affectedJurisdictions.map((j, i) => (
                        <span key={i} className="inline-flex rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] font-medium">
                          {j}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500 dark:text-slate-400">Permit required:</span>
                  <span className={`font-medium ${
                    summaryResult.permitRequired === true ? 'text-amber-700' :
                    summaryResult.permitRequired === false ? 'text-green-700' :
                    'text-slate-400'
                  }`}>
                    {summaryResult.permitRequired === true ? 'Yes' :
                     summaryResult.permitRequired === false ? 'No' :
                     'Unclear'}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ZoningAIPanel;
